/**
 * DateTimePicker.tsx — Beacon-direction date+time picker primitive
 * (Part 5.5b).
 *
 * Two-pane popover (460px): preset chips left, calendar + time right.
 * Calendar supports a three-level drill — day grid ↔ month grid ↔ year
 * grid — by clicking the header. The Beacon design itself has no footer;
 * we add Cancel/Save so the popover's commit stays explicit, matching
 * the rest of the InlineEdit popover convention.
 *
 * Consumers wrap this in a popover/portal of their choice (see
 * InlineEditDateTime for the canonical mount + the todo due-date editor).
 *
 * No external date library; native Date arithmetic. Saved value is an ISO
 * 8601 string. Display is local time.
 *
 * One opt-in variant, PICK mode (`onPickDay`, with `today` / `markedDays` /
 * `markLabel` — MOBILE-2E BOOKER-45, the phone booker's month calendar): a
 * month of days only, a tap is the choice, dots under marked days. A caller
 * that passes none of those props renders exactly as before
 * (DateTimePicker.test.tsx pins it with snapshots taken before the change).
 *
 * See refactor/design/date-picker-directions.md for the locked spec.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import styles from './DateTimePicker.module.css';

const MONTH_NAMES = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];
const MONTH_SHORT = MONTH_NAMES.map((m) => m.slice(0, 3));
const DOW = ['S', 'M', 'T', 'W', 'T', 'F', 'S'];

type DrillLevel = 'day' | 'month' | 'year';

// ── Preset construction ──────────────────────────────────────────────────

interface Preset {
  key: string;
  label: string;
  when: string;
  /** Returns the preset's target date (with the picker's current time
   *  preserved by the consumer — see applyPreset below). */
  resolve: (base: Date) => Date;
}

function dayAfter(d: Date, days: number): Date {
  const next = new Date(d);
  next.setDate(next.getDate() + days);
  return next;
}

function nextMondayOf(d: Date): Date {
  const day = d.getDay();
  const daysUntilMonday = day === 0 ? 1 : day === 1 ? 7 : (8 - day);
  return dayAfter(d, daysUntilMonday);
}

function buildPresets(now: Date): Preset[] {
  const today = new Date(now);
  today.setHours(0, 0, 0, 0);
  const tomorrow = dayAfter(today, 1);
  const in2 = dayAfter(today, 2);
  const nextWeek = nextMondayOf(today);
  const in2Weeks = dayAfter(today, 14);
  const nextMonth = (() => {
    const d = new Date(today);
    d.setMonth(d.getMonth() + 1);
    return d;
  })();
  const fmtDow = (d: Date) => d.toLocaleDateString('en-US', { weekday: 'short' });
  const fmtDayShort = (d: Date) =>
    `${d.toLocaleDateString('en-US', { weekday: 'short' })} ${d.getDate()}`;
  const fmtMonDay = (d: Date) =>
    `${d.toLocaleDateString('en-US', { month: 'short' })} ${d.getDate()}`;
  return [
    { key: 'today',     label: 'Today',       when: fmtDow(today),       resolve: () => today },
    { key: 'tomorrow',  label: 'Tomorrow',    when: fmtDow(tomorrow),    resolve: () => tomorrow },
    { key: 'in2days',   label: 'In 2 days',   when: fmtDow(in2),         resolve: () => in2 },
    { key: 'nextweek',  label: 'Next week',   when: fmtDayShort(nextWeek), resolve: () => nextWeek },
    { key: 'in2weeks',  label: 'In 2 weeks',  when: fmtMonDay(in2Weeks), resolve: () => in2Weeks },
    { key: 'nextmonth', label: 'Next month',  when: fmtMonDay(nextMonth), resolve: () => nextMonth },
  ];
}

// ── Helpers ──────────────────────────────────────────────────────────────

function startOfDay(d: Date): Date {
  const x = new Date(d);
  x.setHours(0, 0, 0, 0);
  return x;
}

function sameDay(a: Date, b: Date): boolean {
  return (
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  );
}

/** Returns a flat 42-cell array (6 weeks × 7 days) covering the visible
 *  calendar for the given month, including leading/trailing days from
 *  adjacent months. */
function buildDayGrid(year: number, month: number): Date[] {
  const firstOfMonth = new Date(year, month, 1);
  const startOffset = firstOfMonth.getDay(); // 0 = Sunday
  const gridStart = dayAfter(firstOfMonth, -startOffset);
  const days: Date[] = [];
  for (let i = 0; i < 42; i++) days.push(dayAfter(gridStart, i));
  return days;
}

/** Returns the 12-year span containing the given year — anchored on
 *  multiples of 12 so the same span renders consistently. */
function yearRangeFor(year: number): { start: number; end: number; years: number[] } {
  const start = year - (year % 12);
  const end = start + 11;
  const years: number[] = [];
  for (let i = 0; i < 12; i++) years.push(start + i);
  return { start, end, years };
}

function isoFromParts(year: number, month: number, day: number, hour: number, minute: number): string {
  // Construct in local time so .toISOString() converts to UTC predictably.
  const d = new Date(year, month, day, hour, minute, 0, 0);
  return d.toISOString();
}

/** Match a draft date against the preset set. Returns the matching
 *  preset key, or 'custom' when none match. Compares date-only (time
 *  doesn't factor — presets all default to midnight). */
function matchPreset(draftDate: Date, presets: Preset[]): string {
  const dayOnly = startOfDay(draftDate);
  for (const p of presets) {
    if (sameDay(p.resolve(new Date()), dayOnly)) return p.key;
  }
  return 'custom';
}

// ── Inline chevron icons ─────────────────────────────────────────────────

const ChevLeft: React.FC = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="15 18 9 12 15 6" />
  </svg>
);

const ChevRight: React.FC = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="9 18 15 12 9 6" />
  </svg>
);

// ── Props ────────────────────────────────────────────────────────────────

export interface DateTimePickerProps {
  /** Current value as ISO 8601 string, or null when unset (defaults to
   *  today at noon for the initial draft). */
  value: string | null;
  /** Receives the next ISO string on Save. */
  onSave: (nextIso: string) => void | Promise<void>;
  /** Cancel closes the picker without committing. */
  onCancel: () => void;
  /** Optional lower bound (ISO 8601 or YYYY-MM-DD). Day cells before this
   *  calendar day render disabled and can't be picked. */
  minDate?: string;
  /** Optional upper bound (ISO 8601 or YYYY-MM-DD). Day cells after this
   *  calendar day render disabled and can't be picked. */
  maxDate?: string;
  /** The phone layout (HubDateField `inline`, in a phone sheet): one column
   *  at the width of its container — the quick picks in a sideways strip
   *  over the month — nothing under 11px, 44px targets, flat. The 460px
   *  two-pane popover runs off a 390px screen. */
  stacked?: boolean;
  /** A day only: no time row (the value keeps its time of day). */
  dateOnly?: boolean;
  /**
   * PICK mode — opt-in (MOBILE-2E BOOKER-45: the phone booker's month
   * calendar, drawn `stacked`). A day tap IS the choice: `onPickDay` gets the
   * day (YYYY-MM-DD, the calendar day shown) at once. `value` is controlled —
   * the picked day follows it, and the month in view jumps to it when it
   * changes. No Quick rail and no Cancel / Save footer (the Beacon design's
   * own "commit on day-click"); the month header does not drill to the month
   * or year grids; Previous / Next stop at the months holding `minDate` and
   * `maxDate`; each day carries `data-date`, `aria-pressed` and a spoken
   * name. The month is ONE Tab stop (the picked day, else the first day in
   * range): the arrows move it a day or a week, across months, never past
   * the bounds; Home / End go to the month's first / last day in range. The
   * frame is borderless — it sits in its caller's band. `onSave` / `onCancel`
   * are not called. Without it the picker renders exactly as before.
   */
  onPickDay?: (day: string) => void;
  /** Opt-in: the calendar's "today" (YYYY-MM-DD) when it is not the device's — the booker's is the host's. */
  today?: string;
  /**
   * Opt-in: days (YYYY-MM-DD) drawn with a small indigo dot under the number.
   * A Saturday or Sunday in the month and in range WITHOUT one reads faint
   * (Kit-Strips: "weekend without openings = faint, no dot").
   */
  markedDays?: readonly string[];
  /** Opt-in: what a dot means, said with a marked day's name ("has open times"). */
  markLabel?: string;
  /** With `dateOnly`: a quick pick or a day in the grid is applied the
   *  moment it is tapped (`onSave` straight away — HubDatePop's grammar), so
   *  nothing is staged and the foot carries Cancel alone. For the phone sheet
   *  whose own Save would otherwise sit beside the picker's (MOBILE-2E TODO+
   *  review round 1: a tapped day never reached the sheet's Save). */
  applyOnPick?: boolean;
}

const WEEKDAY_NAMES = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

/** A local calendar day as YYYY-MM-DD (the day the grid shows). */
function ymdOf(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

const monthIndex = (year: number, month: number): number => year * 12 + month;

/** Parse a bound string (ISO or YYYY-MM-DD) to the start of its local
 *  calendar day, or null when absent/invalid. A bare date is anchored to
 *  local midnight so the bound matches the calendar day the user sees. */
function parseBound(s: string | undefined): Date | null {
  if (!s) return null;
  const iso = /^\d{4}-\d{2}-\d{2}$/.test(s) ? `${s}T00:00:00` : s;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  return startOfDay(d);
}

// ── Component ────────────────────────────────────────────────────────────

const DateTimePicker: React.FC<DateTimePickerProps> = ({
  value, onSave, onCancel, minDate, maxDate, stacked = false, dateOnly = false,
  applyOnPick = false, onPickDay, today: todayOverride, markedDays, markLabel,
}) => {
  const pick = !!onPickDay;
  // Applied on pick only for a day (a time still needs its own Save); pick
  // mode never calls onSave — its day goes to onPickDay.
  const pickApplies = applyOnPick && dateOnly && !pick;
  const applyNow = useCallback(
    (d: Date) => {
      if (!pickApplies) return;
      void onSave(isoFromParts(d.getFullYear(), d.getMonth(), d.getDate(), d.getHours(), d.getMinutes()));
    },
    [pickApplies, onSave],
  );
  const now = useMemo(() => new Date(), []);
  const todayDay = useMemo(() => parseBound(todayOverride), [todayOverride]);
  const today = useMemo(() => todayDay ?? startOfDay(now), [todayDay, now]);
  const presets = useMemo(() => buildPresets(now), [now]);
  const marked = useMemo(() => (markedDays ? new Set(markedDays) : null), [markedDays]);

  const minDay = useMemo(() => parseBound(minDate), [minDate]);
  const maxDay = useMemo(() => parseBound(maxDate), [maxDate]);

  const isOutOfRange = useCallback(
    (d: Date): boolean => {
      const t = startOfDay(d).getTime();
      if (minDay && t < minDay.getTime()) return true;
      if (maxDay && t > maxDay.getTime()) return true;
      return false;
    },
    [minDay, maxDay],
  );

  // Initialize the draft from `value` or default to today @ 12:00.
  const initial = useMemo(() => {
    if (value) {
      const d = new Date(value);
      if (!Number.isNaN(d.getTime())) return d;
    }
    const d = new Date(todayDay ?? now);
    d.setHours(12, 0, 0, 0);
    return d;
  }, [value, now, todayDay]);

  const [draft, setDraft] = useState<Date>(initial);

  // Pick mode's keyboard: the day holding the month's one Tab stop once the
  // arrows (or a tap) moved it, and the day to focus after the next render.
  const [focusDay, setFocusDay] = useState<string | null>(null);
  const pendingFocus = useRef<string | null>(null);
  const gridRef = useRef<HTMLDivElement | null>(null);

  // Calendar view tracks (year, month) the grid is showing; defaults to
  // draft's month but can navigate independently.
  const [viewYear, setViewYear] = useState<number>(initial.getFullYear());
  const [viewMonth, setViewMonth] = useState<number>(initial.getMonth());

  // Pick mode: `value` is controlled — a new value is the picked day, and its month comes into view.
  useEffect(() => {
    if (!pick) return;
    setDraft(initial);
    setViewYear(initial.getFullYear());
    setViewMonth(initial.getMonth());
  }, [pick, initial]);

  // Drill level — day grid (default) / month grid / year grid.
  const [drill, setDrill] = useState<DrillLevel>('day');

  // Brief fade-out when the drill changes so the swap doesn't feel abrupt.
  const [fading, setFading] = useState(false);

  const setDrillWithFade = useCallback((next: DrillLevel) => {
    setFading(true);
    setTimeout(() => {
      setDrill(next);
      setFading(false);
    }, 100);
  }, []);

  // ── Time inputs (hour 1-12, minute 0-59, AM/PM) ────────────────────────
  const ampm: 'AM' | 'PM' = draft.getHours() >= 12 ? 'PM' : 'AM';
  const hour12 = draft.getHours() % 12 === 0 ? 12 : draft.getHours() % 12;
  const minute = draft.getMinutes();

  const [hourStr, setHourStr] = useState(String(hour12));
  const [minuteStr, setMinuteStr] = useState(String(minute).padStart(2, '0'));

  useEffect(() => {
    setHourStr(String(hour12));
    setMinuteStr(String(minute).padStart(2, '0'));
  }, [hour12, minute]);

  const commitHour = useCallback(
    (raw: string) => {
      const n = Math.max(1, Math.min(12, Number(raw) || 12));
      const nextDraft = new Date(draft);
      // Translate 12-hour to 24-hour using current AM/PM.
      const h24 = ampm === 'AM' ? (n === 12 ? 0 : n) : (n === 12 ? 12 : n + 12);
      nextDraft.setHours(h24);
      setDraft(nextDraft);
    },
    [draft, ampm],
  );

  const commitMinute = useCallback(
    (raw: string) => {
      const n = Math.max(0, Math.min(59, Number(raw) || 0));
      const nextDraft = new Date(draft);
      nextDraft.setMinutes(n);
      setDraft(nextDraft);
    },
    [draft],
  );

  const setAmpm = useCallback(
    (next: 'AM' | 'PM') => {
      if (next === ampm) return;
      const nextDraft = new Date(draft);
      const h = nextDraft.getHours();
      if (next === 'AM' && h >= 12) nextDraft.setHours(h - 12);
      if (next === 'PM' && h < 12) nextDraft.setHours(h + 12);
      setDraft(nextDraft);
    },
    [draft, ampm],
  );

  // ── Preset application (preserves draft's current time) ────────────────
  const applyPreset = useCallback(
    (preset: Preset) => {
      const target = preset.resolve(new Date());
      const next = new Date(draft);
      next.setFullYear(target.getFullYear(), target.getMonth(), target.getDate());
      setDraft(next);
      setViewYear(target.getFullYear());
      setViewMonth(target.getMonth());
      if (drill !== 'day') setDrillWithFade('day');
      applyNow(next);
    },
    [draft, drill, setDrillWithFade, applyNow],
  );

  const activePreset = useMemo(() => matchPreset(draft, presets), [draft, presets]);

  // ── Day-grid interaction ───────────────────────────────────────────────
  const dayGrid = useMemo(() => buildDayGrid(viewYear, viewMonth), [viewYear, viewMonth]);

  const selectDay = useCallback(
    (d: Date) => {
      if (isOutOfRange(d)) return;
      const next = new Date(draft);
      next.setFullYear(d.getFullYear(), d.getMonth(), d.getDate());
      setDraft(next);
      setViewYear(d.getFullYear());
      setViewMonth(d.getMonth());
      applyNow(next);
      if (onPickDay) {
        setFocusDay(ymdOf(d));
        onPickDay(ymdOf(d));
      }
    },
    [draft, isOutOfRange, applyNow, onPickDay],
  );

  const navMonth = useCallback((delta: -1 | 1) => {
    let nextMonth = viewMonth + delta;
    let nextYear = viewYear;
    if (nextMonth < 0) {
      nextMonth = 11;
      nextYear -= 1;
    } else if (nextMonth > 11) {
      nextMonth = 0;
      nextYear += 1;
    }
    setViewMonth(nextMonth);
    setViewYear(nextYear);
  }, [viewMonth, viewYear]);

  const navYear = useCallback((delta: -1 | 1) => {
    setViewYear((y) => y + delta);
  }, []);

  const navYearRange = useCallback((delta: -1 | 1) => {
    setViewYear((y) => y + delta * 12);
  }, []);

  // ── Month/year grid interaction ────────────────────────────────────────
  const selectMonth = useCallback(
    (m: number) => {
      setViewMonth(m);
      setDrillWithFade('day');
    },
    [setDrillWithFade],
  );

  const selectYear = useCallback(
    (y: number) => {
      setViewYear(y);
      setDrillWithFade('month');
    },
    [setDrillWithFade],
  );

  // ── Save ───────────────────────────────────────────────────────────────
  const save = useCallback(() => {
    onSave(
      isoFromParts(
        draft.getFullYear(),
        draft.getMonth(),
        draft.getDate(),
        draft.getHours(),
        draft.getMinutes(),
      ),
    );
  }, [draft, onSave]);

  // ── Render ─────────────────────────────────────────────────────────────

  const headerText =
    drill === 'day'
      ? `${MONTH_NAMES[viewMonth]} ${viewYear}`
      : drill === 'month'
        ? String(viewYear)
        : (() => {
            const { start, end } = yearRangeFor(viewYear);
            return `${start}–${end}`;
          })();

  const onHeaderClick = () => {
    if (pick) return; // pick mode: a month of days only
    if (drill === 'day') setDrillWithFade('month');
    else if (drill === 'month') setDrillWithFade('year');
    // year level: no drill up (top of stack)
  };

  const onNavPrev = () => {
    if (drill === 'day') navMonth(-1);
    else if (drill === 'month') navYear(-1);
    else navYearRange(-1);
  };

  const onNavNext = () => {
    if (drill === 'day') navMonth(1);
    else if (drill === 'month') navYear(1);
    else navYearRange(1);
  };

  const yearRange = yearRangeFor(viewYear);

  // Pick mode: Previous / Next stop at the months that hold the bounds.
  const viewAt = monthIndex(viewYear, viewMonth);
  const prevStop = pick && !!minDay && viewAt <= monthIndex(minDay.getFullYear(), minDay.getMonth());
  const nextStop = pick && !!maxDay && viewAt >= monthIndex(maxDay.getFullYear(), maxDay.getMonth());

  // Pick mode: the month's one Tab stop — the day the arrows last reached,
  // else the picked day, else the first day in range, while it is in view.
  const inViewAndRange = (d: Date) => d.getMonth() === viewMonth && d.getFullYear() === viewYear && !isOutOfRange(d);
  let tabStop: string | null = null;
  if (pick) {
    const reached = focusDay ? parseBound(focusDay) : null;
    const stop = [reached, draft].find((d) => !!d && inViewAndRange(d)) ?? dayGrid.find(inViewAndRange);
    tabStop = stop ? ymdOf(stop) : null;
  }

  const clampToRange = (d: Date): Date => {
    if (minDay && d.getTime() < minDay.getTime()) return minDay;
    if (maxDay && d.getTime() > maxDay.getTime()) return maxDay;
    return d;
  };

  const onPickKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    const from = parseBound((e.target as HTMLElement).getAttribute('data-date') ?? tabStop ?? undefined);
    if (!from) return;
    let next: Date;
    if (e.key === 'ArrowLeft') next = dayAfter(from, -1);
    else if (e.key === 'ArrowRight') next = dayAfter(from, 1);
    else if (e.key === 'ArrowUp') next = dayAfter(from, -7);
    else if (e.key === 'ArrowDown') next = dayAfter(from, 7);
    else if (e.key === 'Home') next = new Date(from.getFullYear(), from.getMonth(), 1);
    else if (e.key === 'End') next = new Date(from.getFullYear(), from.getMonth() + 1, 0);
    else return;
    e.preventDefault();
    next = clampToRange(next);
    if (next.getMonth() !== viewMonth || next.getFullYear() !== viewYear) {
      setViewYear(next.getFullYear());
      setViewMonth(next.getMonth());
    }
    const ymd = ymdOf(next);
    setFocusDay(ymd);
    pendingFocus.current = ymd;
  };

  // Focus the day the arrows reached once it is drawn (it may be in the next month).
  useEffect(() => {
    const ymd = pendingFocus.current;
    if (!ymd || !gridRef.current) return;
    pendingFocus.current = null;
    const el = gridRef.current.querySelector<HTMLButtonElement>(`[data-date="${ymd}"]`);
    if (el) el.focus();
  });

  return (
    <div
      className={[styles.root, stacked ? styles.stacked : '', pick ? styles.pick : ''].filter(Boolean).join(' ')}
      data-layout={stacked ? 'stacked' : undefined}
    >
      {pick ? null : (
      <div className={styles.presets}>
        <div className={styles.presetLabel}>Quick</div>
        {presets.map((p) => {
          const isActive = activePreset === p.key;
          return (
            <button
              key={p.key}
              type="button"
              className={`${styles.preset} ${isActive ? styles.presetActive : ''}`.trim()}
              onClick={() => applyPreset(p)}
            >
              <span>{p.label}</span>
              <span className={styles.presetWhen}>{p.when}</span>
            </button>
          );
        })}
        <button
          type="button"
          className={`${styles.preset} ${activePreset === 'custom' ? styles.presetActive : ''}`.trim()}
          // Custom selects itself; clicking is a no-op (it's already
          // implicit when no preset matches).
        >
          <span>Custom</span>
          <span className={styles.presetWhen}>
            {draft.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
          </span>
        </button>
      </div>
      )}

      <div className={styles.right}>
        <div className={styles.head}>
          <button
            type="button"
            className={styles.headLabel}
            onClick={onHeaderClick}
            disabled={drill === 'year' || pick}
            aria-label={
              pick
                ? headerText
                : drill === 'year'
                ? `Year range ${headerText}`
                : drill === 'month'
                  ? `Year ${headerText}, click to switch to year range`
                  : `${headerText}, click to switch to month grid`
            }
          >
            {headerText}
          </button>
          <div className={styles.nav}>
            <button
              type="button"
              className={styles.navBtn}
              onClick={onNavPrev}
              disabled={prevStop}
              aria-label={pick ? 'Previous month' : 'Previous'}
            >
              <ChevLeft />
            </button>
            <button
              type="button"
              className={styles.navBtn}
              onClick={onNavNext}
              disabled={nextStop}
              aria-label={pick ? 'Next month' : 'Next'}
            >
              <ChevRight />
            </button>
          </div>
        </div>

        <div className={`${styles.gridLevel} ${fading ? styles.fading : ''}`.trim()}>
          {drill === 'day' && (
            <div
              className={styles.dayGrid}
              ref={pick ? gridRef : undefined}
              onKeyDown={pick ? onPickKeyDown : undefined}
            >
              {DOW.map((d, i) => (
                <div key={`dow-${i}`} className={styles.dow}>{d}</div>
              ))}
              {dayGrid.map((d, i) => {
                const inMonth = d.getMonth() === viewMonth;
                const isToday = sameDay(d, today);
                const isSelected = sameDay(d, draft);
                const disabled = isOutOfRange(d);
                const ymd = pick || marked ? ymdOf(d) : '';
                const isMarked = !!marked && marked.has(ymd);
                // The strip's grammar: a weekend day in range with nothing marked reads faint.
                const weekend = d.getDay() === 0 || d.getDay() === 6;
                const quiet = !!marked && inMonth && !disabled && !isMarked && !isSelected && !isToday && weekend;
                const cls = [
                  styles.day,
                  !inMonth ? styles.muted : '',
                  disabled ? styles.disabled : '',
                  isToday && isSelected
                    ? styles.todayAndSelected
                    : isToday
                      ? styles.today
                      : '',
                  isSelected && !isToday ? styles.selected : '',
                  marked ? styles.markable : '',
                  quiet ? styles.quiet : '',
                ]
                  .filter(Boolean)
                  .join(' ');
                const spoken = pick
                  ? `${WEEKDAY_NAMES[d.getDay()]} ${MONTH_NAMES[d.getMonth()]} ${d.getDate()}${isToday ? ', today' : ''}${isMarked && markLabel ? `, ${markLabel}` : ''}`
                  : undefined;
                return (
                  <button
                    key={`day-${i}`}
                    type="button"
                    className={cls}
                    disabled={disabled}
                    aria-disabled={disabled || undefined}
                    aria-pressed={pick ? isSelected : undefined}
                    aria-label={spoken}
                    data-date={pick ? ymd : undefined}
                    data-marked={marked ? (isMarked ? 'true' : 'false') : undefined}
                    tabIndex={pick ? (ymd === tabStop ? 0 : -1) : undefined}
                    onFocus={pick ? () => setFocusDay(ymd) : undefined}
                    onClick={() => selectDay(d)}
                  >
                    {d.getDate()}
                    {marked ? (
                      <span className={isMarked ? `${styles.mark} ${styles.markOn}` : styles.mark} aria-hidden="true" />
                    ) : null}
                  </button>
                );
              })}
            </div>
          )}

          {drill === 'month' && (
            <div className={styles.monthGrid}>
              {MONTH_SHORT.map((m, idx) => {
                const isToday = today.getFullYear() === viewYear && today.getMonth() === idx;
                const isSelected = draft.getFullYear() === viewYear && draft.getMonth() === idx;
                const cls = [
                  styles.monthCell,
                  isToday && isSelected
                    ? styles.todayAndSelected
                    : isToday
                      ? styles.today
                      : '',
                  isSelected && !isToday ? styles.selected : '',
                ]
                  .filter(Boolean)
                  .join(' ');
                return (
                  <button
                    key={`m-${idx}`}
                    type="button"
                    className={cls}
                    onClick={() => selectMonth(idx)}
                  >
                    {m}
                  </button>
                );
              })}
            </div>
          )}

          {drill === 'year' && (
            <div className={styles.yearGrid}>
              {yearRange.years.map((y) => {
                const isToday = today.getFullYear() === y;
                const isSelected = draft.getFullYear() === y;
                const cls = [
                  styles.yearCell,
                  isToday && isSelected
                    ? styles.todayAndSelected
                    : isToday
                      ? styles.today
                      : '',
                  isSelected && !isToday ? styles.selected : '',
                ]
                  .filter(Boolean)
                  .join(' ');
                return (
                  <button
                    key={`y-${y}`}
                    type="button"
                    className={cls}
                    onClick={() => selectYear(y)}
                  >
                    {y}
                  </button>
                );
              })}
            </div>
          )}
        </div>

        {dateOnly ? null : (
        <div className={styles.timeRow}>
          <span className={styles.timeLabel}>Time</span>
          <div className={styles.timeInput}>
            <input
              type="text"
              inputMode="numeric"
              value={hourStr}
              onChange={(e) => setHourStr(e.target.value)}
              onBlur={() => commitHour(hourStr)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') (e.target as HTMLInputElement).blur();
              }}
              aria-label="Hour"
            />
            <span className={styles.timeSep}>:</span>
            <input
              type="text"
              inputMode="numeric"
              value={minuteStr}
              onChange={(e) => setMinuteStr(e.target.value)}
              onBlur={() => commitMinute(minuteStr)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') (e.target as HTMLInputElement).blur();
              }}
              aria-label="Minute"
            />
            <div className={styles.ampm}>
              <button
                type="button"
                className={`${styles.ampmBtn} ${ampm === 'AM' ? styles.ampmBtnActive : ''}`.trim()}
                onClick={() => setAmpm('AM')}
              >
                AM
              </button>
              <button
                type="button"
                className={`${styles.ampmBtn} ${ampm === 'PM' ? styles.ampmBtnActive : ''}`.trim()}
                onClick={() => setAmpm('PM')}
              >
                PM
              </button>
            </div>
          </div>
        </div>
        )}

        {pick ? null : (
        <div className={styles.foot}>
          <button type="button" className={styles.footBtn} onClick={onCancel}>
            Cancel
          </button>
          {pickApplies ? null : (
            <button
              type="button"
              className={`${styles.footBtn} ${styles.footBtnPrimary}`}
              onClick={save}
            >
              Save
            </button>
          )}
        </div>
        )}
      </div>
    </div>
  );
};

export default DateTimePicker;
